const CAPTCHA_OPT_OUT = 'data-nocaptcha';
const CAPTCHA_TIMEOUT = 8000;
const CONFIRMATION_GRACE = 5000;
const SUBMITTED_FORM_KEY = 'ajax-form:submitted';
const POSTED_PARAMS = ['customer_posted', 'contact_posted'];

// Session storage can be refused by the browser: a value that could not be written then reads as absent.
const submittedForm = {
  read() {
    try {
      return sessionStorage.getItem(SUBMITTED_FORM_KEY);
    } catch {
      return null;
    }
  },
  write(value) {
    try {
      if (value) sessionStorage.setItem(SUBMITTED_FORM_KEY, value);
      else sessionStorage.removeItem(SUBMITTED_FORM_KEY);
    } catch {
      return;
    }
  },
};

// Read once for the page: several forms compare themselves to it, none may clear it for the others.
const reloadedFormId = submittedForm.read();
submittedForm.write(null);

/**
 * Sends a Shopify customer or contact form without reloading the page.
 *
 * Expects a form rendered with `data-nocaptcha: 'true'`, its fields inside `[data-ajax-form-fields]`
 * and its confirmation inside `[data-ajax-form-success]`. Falls back to a regular submission
 * whenever Shopify does not confirm the request.
 *
 * A subscription (customer form) shows its confirmation as soon as it is submitted, the spam protection
 * and the request running behind it: the fields come back if the request has not left in time (challenge
 * left unanswered), and the regular submission still takes over if Shopify does not confirm.
 * A message (contact form) waits for Shopify's answer, since it would be lost with the page.
 *
 * @fires ajax-form:before-send - `detail.waitUntil(promise)` delays the request, a rejection cancels it.
 */
class AjaxForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.fields = this.querySelector('[data-ajax-form-fields]');
    this.success = this.querySelector('[data-ajax-form-success]');
    this.optimistic = this.form.elements.namedItem('form_type')?.value === 'customer';
    // Capture phase, so ahead of the listeners Shopify's spam protection adds to the form.
    this.addEventListener('submit', this, true);
    this.hookSubmit();
    this.handleReload();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this, true);
    clearTimeout(this.captchaTimer);
    clearTimeout(this.graceTimer);
  }

  // Shopify's spam protection wraps the form's submit method: it gets its token, adds it to the form,
  // then calls the method it found when binding. The form stays closed to it until this one is in place.
  hookSubmit() {
    if (!this.form.hasAttribute(CAPTCHA_OPT_OUT)) return;
    this.form.submit = () => this.send();
    this.form.removeAttribute(CAPTCHA_OPT_OUT);
  }

  // After a regular submission Shopify raises the outcome on every form of the same type on the page:
  // only the form noted at submission keeps it.
  handleReload() {
    const error = this.querySelector('[data-ajax-form-error]');
    const posted = !this.success.hidden;
    if (!posted && !error) return;
    const own = reloadedFormId ? reloadedFormId === this.form.id : window.location.hash === `#${this.form.id}`;
    if (own) {
      this.dataset.outcome = posted ? 'success' : 'error';
      return;
    }
    this.success.hidden = true;
    this.fields.hidden = false;
    error?.remove();
  }

  handleEvent(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy) return;
    submittedForm.write(this.form.id);
    this.dispatchEvent(new CustomEvent('ajax-form:submit', { bubbles: true }));
    if (this.optimistic) this.toggleSuccess(true);
    this.protect();
  }

  // The delay only covers the loading of the spam protection: if it has not bound to the form in time
  // (blocked script), the request leaves without it and Shopify decides what happens next.
  protect() {
    const captcha = window.Shopify?.captcha;
    if (!captcha) {
      this.send();
      return;
    }
    clearTimeout(this.captchaTimer);
    this.captchaTimer = setTimeout(() => this.send(), CAPTCHA_TIMEOUT);
    captcha.protect(this.form, () => {
      clearTimeout(this.captchaTimer);
      this.form.submit();
      // Once bound, the protection answers on its own or shows a challenge the visitor may close, which
      // nothing reports: a confirmation whose request has not left in time gives way to the fields.
      if (!this.optimistic) return;
      clearTimeout(this.graceTimer);
      this.graceTimer = setTimeout(() => {
        if (!this.busy && !this.posted) this.toggleSuccess(false);
      }, CONFIRMATION_GRACE);
    });
  }

  async send() {
    if (this.busy || this.posted) return;
    this.setBusy(true);

    // A field can ask the form to wait for its own work, and keep it from leaving if that work fails.
    const pending = [];
    const detail = { waitUntil: (work) => pending.push(work) };
    this.dispatchEvent(new CustomEvent('ajax-form:before-send', { bubbles: true, detail }));
    try {
      await Promise.all(pending);
    } catch {
      submittedForm.write(null);
      this.setBusy(false);
      this.toggleSuccess(false);
      return;
    }

    let posted = false;
    try {
      // A file cannot travel in a URL-encoded body, and Shopify's contact form does not accept files.
      const entries = [...new FormData(this.form)].filter(([, value]) => typeof value === 'string');
      const response = await fetch(this.form.action, {
        method: 'POST',
        headers: { Accept: 'text/html' },
        body: new URLSearchParams(entries),
      });
      const { searchParams } = new URL(response.url);
      posted = response.ok && POSTED_PARAMS.some((param) => searchParams.has(param));
    } catch {
      posted = false;
    }

    if (!posted) {
      // Shopify did not confirm (challenge to display, rejected field, network): the form leaves through
      // a regular submission, which reloads the page and shows the answer.
      HTMLFormElement.prototype.submit.call(this.form);
      return;
    }
    this.posted = true;
    submittedForm.write(null);
    this.setBusy(false);
    this.form.reset();
    this.toggleSuccess(true);
    this.dispatchEvent(new CustomEvent('ajax-form:success', { bubbles: true }));
  }

  // Focus follows what is shown: the confirmation, or the first field when it gives way to them.
  toggleSuccess(shown) {
    if (this.success.hidden === !shown) return;
    const focused = this.success.contains(document.activeElement);
    this.fields.hidden = shown;
    this.success.hidden = !shown;
    if (shown) {
      this.querySelector('[data-ajax-form-error]')?.remove();
      this.success.focus({ preventScroll: true });
      this.dataset.outcome = 'success';
      return;
    }
    delete this.dataset.outcome;
    if (!focused) return;
    this.fields.querySelector('input:not([type="hidden"]), select, textarea')?.focus({ preventScroll: true });
  }

  setBusy(busy) {
    this.busy = busy;
    this.toggleAttribute('data-busy', busy);
    this.form.setAttribute('aria-busy', String(busy));
  }
}

if (!customElements.get('ajax-form')) {
  customElements.define('ajax-form', AjaxForm);
}
